import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from 'next/link';
import Icon from '../Icon';
import { withQuery } from '../ManualSearch/ManualSearch';
import GlossaryText from '../GlossaryText';
import styles from './ManualNav.module.scss';

// Must match ManualSection's scroll-margin-top.
const SCROLL_OFFSET = 96;

const ManualNav = ({
    chapters = [],
    activeAnchor = '',
    matchedHrefs = [],
    searchQuery = '',
}) => {
    const [isMobileExpanded, setIsMobileExpanded] = useState(false);
    const [expanded, setExpanded] = useState(() => {
        const activeIndex = chapters.findIndex((chapter) => chapter.isActive);
        return activeIndex >= 0 ? [activeIndex] : [];
    });
    const [observedAnchor, setObservedAnchor] = useState('');

    const matchCount = (chapter) =>
        matchedHrefs.filter((href) => href.split('#')[0] === chapter.href)
            .length;
    const isSectionMatched = (chapter, section) =>
        matchedHrefs.includes(`${chapter.href}#${section.anchor}`);

    // A matching link keeps the search, so the next page highlights it too.
    const linkTo = (href, isMatch) =>
        isMatch && searchQuery ? withQuery(href, searchQuery) : href;

    const activeIndex = chapters.findIndex((chapter) => chapter.isActive);
    const activeChapter = activeIndex >= 0 ? chapters[activeIndex] : undefined;

    // The nav stays mounted across client side navigation, so the state from
    // the useState initialiser would keep pointing at the previous chapter.
    useEffect(() => {
        setExpanded(activeIndex >= 0 ? [activeIndex] : []);
    }, [activeIndex]);
    const anchorKey = (activeChapter?.sections || [])
        .map((section) => section.anchor)
        .filter(Boolean)
        .join('|');

    useEffect(() => {
        const anchors = anchorKey ? anchorKey.split('|') : [];
        const elements = anchors
            .map((anchor) => document.getElementById(anchor))
            .filter(Boolean);

        if (!elements.length) {
            return undefined;
        }

        let frame = null;

        const update = () => {
            frame = null;

            // A short last section may never reach the offset.
            const atBottom =
                window.innerHeight + window.scrollY >=
                document.documentElement.scrollHeight - 2;

            if (atBottom) {
                setObservedAnchor(elements[elements.length - 1].id);
                return;
            }

            let current = elements[0].id;

            elements.forEach((element) => {
                if (element.getBoundingClientRect().top - SCROLL_OFFSET <= 0) {
                    current = element.id;
                }
            });

            setObservedAnchor(current);
        };

        const onScroll = () => {
            if (frame === null) {
                frame = window.requestAnimationFrame(update);
            }
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);

        return () => {
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }

            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [anchorKey]);

    if (!chapters.length) {
        return null;
    }

    const toggleChapter = (index) => {
        setExpanded((prevExpanded) =>
            prevExpanded.includes(index)
                ? prevExpanded.filter((item) => item !== index)
                : [...prevExpanded, index]
        );
    };

    const currentAnchor = observedAnchor || activeAnchor;

    return (
        <nav
            aria-label="Manual contents"
            className={classNames(styles['ManualNav'], {
                [styles['ManualNav--Expanded']]: isMobileExpanded,
            })}>
            <button
                type="button"
                className={styles['ManualNav__MobileToggle']}
                aria-expanded={isMobileExpanded}
                aria-controls="manual-nav-chapters"
                onClick={() => setIsMobileExpanded(!isMobileExpanded)}>
                <span>
                    <span className={styles['ManualNav__MobileTag']}>Menu</span>
                    {!!activeChapter && (
                        <span className={styles['ManualNav__MobileTitle']}>
                            <GlossaryText text={activeChapter.title} />
                        </span>
                    )}
                </span>
                <span className={styles['ManualNav__MobileIcon']}>
                    <Icon
                        type="chevronThin"
                        modifiers={['ManualNavMobileChevron']}
                    />
                </span>
            </button>

            <div
                id="manual-nav-chapters"
                className={styles['ManualNav__Container']}>
                <ul className={styles['ManualNav__Chapters']}>
                    {chapters.map((chapter, index) => {
                        const sections = chapter.sections || [];
                        const isExpanded = expanded.includes(index);
                        const sectionsId = `manual-nav-sections-${index}`;

                        return (
                            <li
                                key={index}
                                className={styles['ManualNav__Chapter']}>
                                <div
                                    className={classNames(
                                        styles['ManualNav__ChapterRow'],
                                        {
                                            [styles[
                                                'ManualNav__ChapterRow--Active'
                                            ]]: chapter.isActive,
                                        }
                                    )}>
                                    <span
                                        className={
                                            styles['ManualNav__ToggleSlot']
                                        }>
                                        {!!sections.length && (
                                            <button
                                                type="button"
                                                className={classNames(
                                                    styles['ManualNav__Toggle'],
                                                    {
                                                        [styles[
                                                            'ManualNav__Toggle--Expanded'
                                                        ]]: isExpanded,
                                                    }
                                                )}
                                                aria-expanded={isExpanded}
                                                aria-controls={sectionsId}
                                                aria-label={`${chapter.title} sections`}
                                                onClick={() =>
                                                    toggleChapter(index)
                                                }>
                                                <span
                                                    className={
                                                        styles[
                                                            'ManualNav__ToggleIcon'
                                                        ]
                                                    }>
                                                    <Icon
                                                        type="chevronThin"
                                                        modifiers={[
                                                            'ManualNavChevron',
                                                        ]}
                                                    />
                                                </span>
                                            </button>
                                        )}
                                    </span>

                                    {!!chapter.icon && (
                                        <span
                                            className={
                                                styles['ManualNav__Icon']
                                            }>
                                            <Icon
                                                type={chapter.icon}
                                                modifiers={['ManualNavIcon']}
                                            />
                                        </span>
                                    )}

                                    <Link
                                        href={linkTo(
                                            chapter.href,
                                            matchCount(chapter) > 0
                                        )}
                                        className={
                                            styles['ManualNav__ChapterLink']
                                        }
                                        aria-current={
                                            chapter.isActive
                                                ? 'page'
                                                : undefined
                                        }
                                        onClick={() =>
                                            setIsMobileExpanded(false)
                                        }>
                                        <GlossaryText text={chapter.title} />
                                    </Link>

                                    {matchCount(chapter) > 0 && (
                                        <span
                                            className={
                                                styles['ManualNav__MatchCount']
                                            }>
                                            {matchCount(chapter)}
                                            <span className="sr-only">
                                                {' '}
                                                search matches
                                            </span>
                                        </span>
                                    )}
                                </div>

                                {!!sections.length && (
                                    <div
                                        id={sectionsId}
                                        className={classNames(
                                            styles['ManualNav__Sections'],
                                            {
                                                [styles[
                                                    'ManualNav__Sections--Expanded'
                                                ]]: isExpanded,
                                            }
                                        )}>
                                        <ul
                                            className={
                                                styles[
                                                    'ManualNav__SectionsInner'
                                                ]
                                            }>
                                            {sections.map(
                                                (section, sectionIndex) => (
                                                    <li key={sectionIndex}>
                                                        <Link
                                                            href={linkTo(
                                                                `${chapter.href}#${section.anchor}`,
                                                                isSectionMatched(
                                                                    chapter,
                                                                    section
                                                                )
                                                            )}
                                                            className={classNames(
                                                                styles[
                                                                    'ManualNav__SectionLink'
                                                                ],
                                                                {
                                                                    [styles[
                                                                        'ManualNav__SectionLink--Active'
                                                                    ]]:
                                                                        chapter.isActive &&
                                                                        currentAnchor ===
                                                                            section.anchor,
                                                                }
                                                            )}
                                                            onClick={() =>
                                                                setIsMobileExpanded(
                                                                    false
                                                                )
                                                            }>
                                                            {/* The link is a flex box, which would drop
                                                                the spaces between glossary spans. */}
                                                            <span>
                                                                <GlossaryText
                                                                    text={
                                                                        section.title
                                                                    }
                                                                />
                                                            </span>
                                                        </Link>
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </nav>
    );
};

ManualNav.propTypes = {
    chapters: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            href: PropTypes.string,
            icon: PropTypes.string,
            isActive: PropTypes.bool,
            sections: PropTypes.arrayOf(
                PropTypes.shape({
                    title: PropTypes.string,
                    anchor: PropTypes.string,
                })
            ),
        })
    ),
    activeAnchor: PropTypes.string,
    matchedHrefs: PropTypes.arrayOf(PropTypes.string),
    searchQuery: PropTypes.string,
};

export default ManualNav;
